import React, { useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import PaymentHistory from './PaymentHistory';

const Profile = () => {
  const [userData, setUserData] = useState(null);
  const [formData, setFormData] = useState({});
  const [activeTab, setActiveTab] = useState('info'); // Add tab state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userRef = doc(db, 'users', user.uid);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const data = userSnap.data();
            setUserData(data);
            setFormData(data);
          } else {
            setError('User data not found.');
          }
        } catch (err) {
          setError('Failed to load user data: ' + err.message);
        } finally {
          setLoading(false);
        }
      } else {
        setError('You must be logged in to view this page.');
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, [auth]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      const userRef = doc(db, 'users', auth.currentUser.uid);
      const updatedData = {
        displayName: formData.displayName,
        plan: formData.plan,
        updatedAt: new Date().toISOString(),
      };
      await updateDoc(userRef, updatedData);
      setUserData(updatedData);
      setSuccess('Profile updated successfully!');
    } catch (err) {
      setError('Failed to update profile: ' + err.message);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-gray-600">Loading...</div>;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-100 p-6 rounded-lg shadow-md text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors duration-300"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!userData) {
    return <div className="min-h-screen flex items-center justify-center text-gray-600">No user data available.</div>;
  }

  return (
    <div className="profile p-6 font-['Noto Sans'] max-w-3xl mx-auto">
      <h2 className="text-3xl font-bold mb-6 text-gray-800">Profile</h2>

      {/* Tab Navigation */}
      <div className="mb-6 border-b border-gray-300">
        <button
          onClick={() => setActiveTab('info')}
          className={`mr-4 pb-2 text-lg font-semibold ${
            activeTab === 'info' ? 'border-b-2 border-purple-600 text-purple-600' : 'text-gray-600'
          }`}
        >
          User Information
        </button>
        <button
          onClick={() => setActiveTab('payment')}
          className={`pb-2 text-lg font-semibold ${
            activeTab === 'payment' ? 'border-b-2 border-purple-600 text-purple-600' : 'text-gray-600'
          }`}
        >
          Payment History
        </button>
      </div>

      {/* User Information Tab */}
      {activeTab === 'info' && (
        <>
          {success && (
            <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg">
              {success}
            </div>
          )}
          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 border border-gray-300">
            <h3 className="text-2xl font-bold mb-4 text-gray-800">User Information</h3>
            <div className="space-y-4">
              {/* Read-Only Fields */}
              <div>
                <label className="block text-gray-600 mb-1">Username</label>
                <input
                  type="text"
                  value={formData.username || ''}
                  className="w-full p-2 border rounded-lg border-gray-300 bg-gray-100 text-gray-600"
                  disabled
                />
              </div>
              <div>
                <label className="block text-gray-600 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email || ''}
                  className="w-full p-2 border rounded-lg border-gray-300 bg-gray-100 text-gray-600"
                  disabled
                />
              </div>
              <div>
                <label className="block text-gray-600 mb-1">Credits</label>
                <input
                  type="number"
                  value={formData.credits || 0}
                  className="w-full p-2 border rounded-lg border-gray-300 bg-gray-100 text-gray-600"
                  disabled
                />
              </div>

              {/* Editable Fields */}
              <div>
                <label className="block text-gray-600 mb-1">Display Name</label>
                <input
                  type="text"
                  name="displayName"
                  value={formData.displayName || ''}
                  onChange={handleChange}
                  className="w-full p-2 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="e.g., John Doe"
                />
              </div>
              <div>
                <label className="block text-gray-600 mb-1">Plan</label>
                <select
                  name="plan"
                  value={formData.plan || 'basic'}
                  onChange={handleChange}
                  className="w-full p-2 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  disabled // Disable manual plan changes; handled by Stripe
                >
                  <option value="basic">Basic</option>
                  <option value="pro">Pro</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <button
                type="submit"
                className="px-6 py-2 rounded-lg font-semibold bg-purple-600 text-white hover:bg-purple-700 transition-colors duration-300"
              >
                Save Changes
              </button>
            </div>
          </form>
        </>
      )}

      {/* Payment History Tab */}
      {activeTab === 'payment' && <PaymentHistory />}
    </div>
  );
};

export default Profile;